import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';


const AddEmployeeModal = ({ user, onClose, onEmployeeAdded }) => {
        const [addType, setAddType] = useState('invite'); // 'invite' or 'enroll' 
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Manager',
        properties: [],
    });

    const [isExistingUser, setIsExistingUser] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [availableProperties, setAvailableProperties] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- FIX: Filter properties based on the search term ---
    const filteredProperties = availableProperties.filter(prop => 
        prop.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // --- FIX: Select All/Deselect All now works on the filtered list ---
    const handleSelectAll = () => {
        const allIds = filteredProperties.map(prop => prop.id);
        setFormData(prev => ({ ...prev, properties: allIds }));
    };
    const handleDeselectAll = () => {
        setFormData(prev => ({ ...prev, properties: [] }));
    };

    // Fetch properties user can manage
    useEffect(() => {
        const fetchUserProperties = async () => {
            try {
                const props = await api.getProperties();
                setAvailableProperties(props);
            } catch (error) {
                toast.error('Could not load properties list.');
            }
        };
        fetchUserProperties();
    }, []);
    
// --- FIX: NEW ROBUST EFFECT FOR DEBOUNCED EMAIL LOOKUP ---
    // This effect handles the email lookup and form state changes
    useEffect(() => {
        const email = formData.email.trim();
const delay = 500; // 500ms debounce delay

        if (!email) {
// Reset state if email is cleared
            setIsExistingUser(false);
            return;
        }

        // Log to console to verify this effect is running.
        console.log('Email lookup triggered for:', email);

        const handler = setTimeout(async () => {
            setIsCheckingEmail(true);
            try {
                const existingUser = await api.getUserByEmail(email);

                // User found, update form data and state
                setFormData(prev => ({
                    ...prev,
                    firstName: existingUser.first_name,
                    lastName: existingUser.last_name,
                    phone: existingUser.phone,
                    role: existingUser.role,
                }));
                setIsExistingUser(true);
                toast.success(`Existing user '${existingUser.first_name}' found.`);
            } catch (error) {
// User not found (404), reset to a new user state
                if (error.statusCode === 404) {
                    if (isExistingUser) {
                        setFormData(prev => ({ ...prev, firstName: '', lastName: '', phone: '', role: 'Manager' }));
                    }
                    setIsExistingUser(false);
                } else {
// Handle other API errors
                    toast.error(error.message || 'Failed to check email address.');
                }
            } finally {
                setIsCheckingEmail(false);
            }
        }, delay);

// Cleanup function to cancel the timeout if the email changes again
        return () => {
            clearTimeout(handler);
        };
    }, [formData.email]); // Effect runs whenever the email address changes

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePropertyChange = (propertyId) => {
        setFormData(prev => {
            const newProperties = prev.properties.includes(propertyId)
                ? prev.properties.filter(id => id !== propertyId)
                : [...prev.properties, propertyId];
            return { ...prev, properties: newProperties };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (formData.properties.length === 0) {
            toast.error('At least one property must be assigned.');
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await api.createAdminUser({
                ...formData,
                isExistingUser
            });
            toast.success(response.message);
            onEmployeeAdded();
            onClose();
        } catch (error) {
            toast.error(error.message || 'Failed to process request.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const canCreateOwner = user.role === 'SuperAdmin';

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h2>Add a New Person</h2>
                
                <div className="add-type-selector">
                    <button 
                        className={`selector-btn ${addType === 'invite' ? 'active' : ''}`}
                        onClick={() => setAddType('invite')}
                    >
                        Invite Owner or Manager
                    </button>
                    <button 
                        className={`selector-btn ${addType === 'enroll' ? 'active' : ''}`}
                        onClick={() => setAddType('enroll')}
                    >
                        Enroll Staff on Device
                    </button>
                </div>

                {addType === 'invite' ? (
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label className="form-label required">Email Address</label>
                            <input type="email" name="email" className="form-input" value={formData.email} onChange={handleInputChange} required />
                            {isCheckingEmail && <small>Checking email...</small>}
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label required">First Name</label>
                                <input type="text" name="firstName" className="form-input" value={formData.firstName} onChange={handleInputChange} required readOnly={isExistingUser} />
                            </div>
                            <div className="form-group">
                                <label className="form-label required">Last Name</label>
                                <input type="text" name="lastName" className="form-input" value={formData.lastName} onChange={handleInputChange} required readOnly={isExistingUser} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label required">Phone Number</label>
                                <input type="tel" name="phone" className="form-input" value={formData.phone} onChange={handleInputChange} required readOnly={isExistingUser} />
                            </div>
                        <div className="form-group">
                            <label className="form-label required">Role</label>
                                                        <select name="role" className="form-input form-select" value={formData.role} onChange={handleInputChange} required disabled={isExistingUser}>
                                {canCreateOwner && <option value="Owner">Owner</option>}
                                <option value="Manager">Manager</option>
                                <option value="Accountant">Accountant</option>
{isExistingUser &&                                 <option value="Staff">Staff</option>}
                            </select>
</div>
                        </div>
                        
                        <div className="form-group">
                            <label className="form-label required">Assign to Properties</label>
                                <div className="property-list-actions">
                                <input 
                                    type="text"
                                    className="form-input search-input-small"
                                    placeholder="Filter properties..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    // FIX: Prevent 'Enter' key from submitting the form
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                <div className="bulk-actions">
                                    <button type="button" className="action-link" onClick={handleSelectAll}>Select All</button>
                                    <span>|</span>
                                    <button type="button" className="action-link" onClick={handleDeselectAll}>Deselect All</button>
                                </div>
                            </div>
                            <div className="properties-checkbox-group">
                                {/* FIX: Map over the FILTERED properties instead of the full list */}
                                {filteredProperties.map(prop => (
                                    <label key={prop.id} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.properties.includes(prop.id)}
                                            onChange={() => handlePropertyChange(prop.id)}
                                        />
                                        {prop.name}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="button button-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                            <button type="submit" className="button action-button" disabled={isSubmitting || isCheckingEmail}>
                                {isSubmitting 
                                    ? (isExistingUser ? 'Assigning...' : 'Sending Invite...')
                                    : (isExistingUser ? 'Assign to Properties' : 'Send Invite')
                                }
                            </button>
                        </div>
                    </form>
                ) : (
                                        <div className="enroll-placeholder">
                        <p>Staff enrollment is done from the Device Management screen.</p>
                        <p>Please navigate to the Devices tab, select a device, and use the "Add Employee (FP)" action.</p>
                         <div className="modal-actions" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                            <button type="button" className="button button-secondary" onClick={onClose}>Close</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddEmployeeModal;